import {
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Link,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";

import RolePill from "./RolePill";
import { deriveEffectivePermissions } from "../../pages/Access/permissions";
import {
  displayName,
  initialsOf,
  stringToColor,
} from "../../pages/Access/selectors";

// SCK-550 — canonical customer-facing reference for the role +
// capability matrix. The href intentionally goes to clue2app.ai (not
// this repo's inline docs) so a single source of truth serves the
// drawer, the CLI --help, and the docs site.
const ROLES_AND_PERMISSIONS_DOC_URL =
  "https://clue2app.ai/docs/roles-and-permissions";

/**
 * EffectivePermissionsDrawer — read-only right-side drawer that
 * translates a Member's current record into an IAM-style verb + count
 * + resource-type list.
 *
 * All data is a client-side aggregate of the member data already
 * loaded for the Members table (member.projects, member.role,
 * member.canCreateProjects). No new coord API calls per SCK-470.
 *
 * Props:
 *   open        — boolean; drawer visibility
 *   onClose     — () => void
 *   member      — Member record or null
 *   memberList  — full members array; needed by deriveDisplayRole for
 *                 the deterministic OWNER pick
 *   currentUser — the JWT-decoded caller; enables the SYSTEM overlay
 *                 when the caller is looking at their own row
 */
export default function EffectivePermissionsDrawer({
  open,
  onClose,
  member,
  memberList,
  currentUser,
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const { role, statements } = deriveEffectivePermissions(
    member,
    memberList,
    currentUser
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 440 },
          maxWidth: "100vw",
        },
      }}
    >
      <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: "0.1em" }}>
            Effective permissions
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close effective permissions drawer"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Identity row */}
        {member && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                fontSize: 16,
                fontWeight: 700,
                bgcolor: stringToColor(member.userId || member.email),
              }}
            >
              {initialsOf(member)}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontWeight: 700 }} noWrap>
                {displayName(member)}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary" }}
                noWrap
                title={member.email}
              >
                {member.email}
              </Typography>
              <Box sx={{ mt: 0.75 }}>
                <RolePill role={role} preview />
              </Box>
            </Box>
          </Stack>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Statements list */}
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mb: 1.5, fontWeight: 500 }}
        >
          This user can:
        </Typography>

        <Stack
          spacing={1.25}
          sx={{ flex: 1, overflowY: "auto", pr: 0.5 }}
          role="list"
          aria-label="Effective permission statements"
        >
          {statements.map((s, i) => (
            <Box
              key={`${s.verb}-${s.resource}-${i}`}
              role="listitem"
              sx={{
                p: 1.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                background: isDarkMode
                  ? "rgba(255,255,255,0.02)"
                  : "rgba(0,0,0,0.015)",
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <ShieldOutlinedIcon
                  fontSize="small"
                  sx={{ color: "text.secondary", mt: 0.25 }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 600 }}>
                    <Box component="span" sx={{ mr: 0.75 }}>
                      {s.verb}
                    </Box>
                    {s.count != null && (
                      <Chip
                        component="span"
                        size="small"
                        label={s.count}
                        sx={{
                          height: 20,
                          fontSize: 12,
                          fontWeight: 700,
                          mr: 0.75,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      />
                    )}
                    <Box
                      component="span"
                      sx={{ color: "text.secondary", fontWeight: 500 }}
                    >
                      {s.resource}
                    </Box>
                  </Typography>
                  {s.detail && (
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", display: "block", mt: 0.5 }}
                    >
                      {s.detail}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>

        {/* Footnote */}
        <Typography
          variant="caption"
          sx={{ color: "text.disabled", mt: 2, display: "block" }}
        >
          Read-only preview. Enforcement of these labels lands with Phase 2
          guards (SCK-468 / SCK-469).
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", mt: 1, display: "block" }}
        >
          <Link
            href={ROLES_AND_PERMISSIONS_DOC_URL}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            Full roles &amp; permissions reference
            <OpenInNewIcon sx={{ fontSize: 12 }} />
          </Link>
        </Typography>
      </Box>
    </Drawer>
  );
}
